import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from 'react';

export type NodeKind = 'state' | 'machine' | 'any' | 'entry' | 'up';

export interface GraphNode {
  key: string;
  label: string;
  /** Second line (motion summary). */
  detail?: string;
  kind: NodeKind;
  isDefault?: boolean;
  /** Currently playing in a running preview (unused for now, styled). */
  active?: boolean;
  x: number;
  y: number;
}

export interface GraphEdge {
  id: string;
  from: string;
  to: string;
  label?: string;
}

export const NODE_W = 160;
export const NODE_H = 40;

type Drag =
  | { kind: 'node'; key: string; dx: number; dy: number; moved: boolean }
  | { kind: 'connect'; from: string }
  | { kind: 'pan'; x: number; y: number; px: number; py: number };

/**
 * The state machine canvas: SVG nodes and transition arrows. Drag a node to
 * move it, drag from its right-hand handle onto another node to connect,
 * drag the background (or middle mouse) to pan, wheel to zoom.
 */
export function StateGraph({
  nodes,
  edges,
  selectedNode,
  selectedEdge,
  onSelectNode,
  onSelectEdge,
  onMoveNode,
  onConnect,
  onOpen,
  onBackground,
  onContextMenu,
}: {
  nodes: GraphNode[];
  edges: GraphEdge[];
  selectedNode: string | null;
  selectedEdge: string | null;
  onSelectNode: (key: string) => void;
  onSelectEdge: (id: string) => void;
  onMoveNode: (key: string, x: number, y: number) => void;
  onConnect: (from: string, to: string) => void;
  onOpen: (key: string) => void;
  onBackground: () => void;
  onContextMenu?: (x: number, y: number, graph: { x: number; y: number }, node: string | null) => void;
}) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [view, setView] = useState({ x: 40, y: 40, z: 1 });
  const [drag, setDrag] = useState<Drag | null>(null);
  const [temp, setTemp] = useState<{ key: string; x: number; y: number } | null>(null);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);

  // Reset the view when the node set changes identity (other layer / machine).
  const signature = nodes.map((n) => n.key).join('|');
  const lastSig = useRef(signature);
  useEffect(() => {
    if (lastSig.current.split('|')[0] !== signature.split('|')[0]) setView({ x: 40, y: 40, z: 1 });
    lastSig.current = signature;
  }, [signature]);

  const pos = (n: GraphNode) => (temp && temp.key === n.key ? { x: temp.x, y: temp.y } : { x: n.x, y: n.y });
  const byKey = new Map(nodes.map((n) => [n.key, n]));

  function toGraph(clientX: number, clientY: number) {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    return { x: (clientX - r.left - view.x) / view.z, y: (clientY - r.top - view.y) / view.z };
  }

  function nodeAt(p: { x: number; y: number }): GraphNode | null {
    for (let i = nodes.length - 1; i >= 0; i--) {
      const n = nodes[i]!;
      const q = pos(n);
      if (p.x >= q.x && p.x <= q.x + NODE_W && p.y >= q.y && p.y <= q.y + NODE_H) return n;
    }
    return null;
  }

  function onPointerDown(e: ReactPointerEvent<SVGSVGElement>) {
    if (e.button === 2) return;
    const target = e.target as Element;
    const p = toGraph(e.clientX, e.clientY);
    svgRef.current?.setPointerCapture(e.pointerId);
    const port = target.closest('[data-port]');
    const nodeEl = target.closest('[data-node]');
    const edgeEl = target.closest('[data-edge]');
    if (e.button === 1 || (!port && !nodeEl && !edgeEl)) {
      setDrag({ kind: 'pan', x: e.clientX, y: e.clientY, px: view.x, py: view.y });
      if (e.button === 0) onBackground();
      return;
    }
    if (port) {
      const key = port.getAttribute('data-port')!;
      setDrag({ kind: 'connect', from: key });
      setCursor(p);
      return;
    }
    if (edgeEl) {
      onSelectEdge(edgeEl.getAttribute('data-edge')!);
      return;
    }
    if (nodeEl) {
      const key = nodeEl.getAttribute('data-node')!;
      const n = byKey.get(key);
      if (!n) return;
      onSelectNode(key);
      setDrag({ kind: 'node', key, dx: p.x - n.x, dy: p.y - n.y, moved: false });
    }
  }

  function onPointerMove(e: ReactPointerEvent<SVGSVGElement>) {
    if (!drag) return;
    if (drag.kind === 'pan') {
      setView((v) => ({ ...v, x: drag.px + e.clientX - drag.x, y: drag.py + e.clientY - drag.y }));
      return;
    }
    const p = toGraph(e.clientX, e.clientY);
    if (drag.kind === 'connect') setCursor(p);
    else {
      const x = Math.round((p.x - drag.dx) / 10) * 10;
      const y = Math.round((p.y - drag.dy) / 10) * 10;
      setTemp({ key: drag.key, x, y });
      if (!drag.moved) setDrag({ ...drag, moved: true });
    }
  }

  function onPointerUp(e: ReactPointerEvent<SVGSVGElement>) {
    const d = drag;
    setDrag(null);
    if (!d) return;
    if (d.kind === 'connect') {
      const target = nodeAt(toGraph(e.clientX, e.clientY));
      setCursor(null);
      if (target && target.key !== d.from) onConnect(d.from, target.key);
    } else if (d.kind === 'node' && d.moved && temp) {
      onMoveNode(temp.key, temp.x, temp.y);
      setTemp(null);
    }
  }

  function onWheel(e: ReactWheelEvent<SVGSVGElement>) {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r) return;
    const z = Math.min(2.5, Math.max(0.3, view.z * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
    const mx = e.clientX - r.left;
    const my = e.clientY - r.top;
    setView({ z, x: mx - ((mx - view.x) / view.z) * z, y: my - ((my - view.y) / view.z) * z });
  }

  // Edge geometry: center to center, clipped to the rectangles; opposite
  // pairs are offset so both stay visible.
  const pairs = new Set(edges.map((e) => `${e.from}>${e.to}`));
  const clip = (cx: number, cy: number, dx: number, dy: number) => {
    const hx = NODE_W / 2;
    const hy = NODE_H / 2;
    const t = Math.min(dx !== 0 ? hx / Math.abs(dx) : Infinity, dy !== 0 ? hy / Math.abs(dy) : Infinity);
    return { x: cx + dx * t, y: cy + dy * t };
  };
  const grouped = new Map<string, number>();

  return (
    <svg
      ref={svgRef}
      className="anim-graph"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onWheel={onWheel}
      onContextMenu={(e) => {
        e.preventDefault();
        const p = toGraph(e.clientX, e.clientY);
        onContextMenu?.(e.clientX, e.clientY, p, nodeAt(p)?.key ?? null);
      }}
      onDoubleClick={(e) => {
        const n = nodeAt(toGraph(e.clientX, e.clientY));
        if (n) onOpen(n.key);
      }}
    >
      <defs>
        <pattern id="anim-grid" width={20 * view.z} height={20 * view.z} patternUnits="userSpaceOnUse" x={view.x} y={view.y}>
          <circle cx={1} cy={1} r={1} className="anim-grid-dot" />
        </pattern>
        <marker id="anim-arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" className="anim-arrow" />
        </marker>
        <marker id="anim-arrow-sel" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" className="anim-arrow is-selected" />
        </marker>
      </defs>
      <rect width="100%" height="100%" fill="url(#anim-grid)" />
      <g transform={`translate(${view.x} ${view.y}) scale(${view.z})`}>
        {edges.map((edge) => {
          const a = byKey.get(edge.from);
          const b = byKey.get(edge.to);
          if (!a || !b || a === b) return null;
          const pa = pos(a);
          const pb = pos(b);
          const ca = { x: pa.x + NODE_W / 2, y: pa.y + NODE_H / 2 };
          const cb = { x: pb.x + NODE_W / 2, y: pb.y + NODE_H / 2 };
          let dx = cb.x - ca.x;
          let dy = cb.y - ca.y;
          const len = Math.hypot(dx, dy) || 1;
          const nx = -dy / len;
          const ny = dx / len;
          const pairKey = `${edge.from}>${edge.to}`;
          const nth = grouped.get(pairKey) ?? 0;
          grouped.set(pairKey, nth + 1);
          const off = (pairs.has(`${edge.to}>${edge.from}`) ? 7 : 0) + nth * 7;
          const s = clip(ca.x, ca.y, dx, dy);
          const t = clip(cb.x, cb.y, -dx, -dy);
          const x1 = s.x + nx * off;
          const y1 = s.y + ny * off;
          const x2 = t.x + nx * off;
          const y2 = t.y + ny * off;
          dx = x2 - x1;
          dy = y2 - y1;
          const mx = x1 + dx * 0.5;
          const my = y1 + dy * 0.5;
          const selected = edge.id === selectedEdge;
          return (
            <g key={edge.id} data-edge={edge.id} className={`anim-edge${selected ? ' is-selected' : ''}`}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} className="anim-edge-hit" />
              <line x1={x1} y1={y1} x2={x2} y2={y2} className="anim-edge-line" />
              <path
                d={`M${mx - dx * 0.0001},${my - dy * 0.0001} L${mx},${my}`}
                className="anim-edge-mid"
                markerEnd={selected ? 'url(#anim-arrow-sel)' : 'url(#anim-arrow)'}
              />
              {edge.label && (
                <text x={mx + nx * 10} y={my + ny * 10} className="anim-edge-label">
                  {edge.label}
                </text>
              )}
            </g>
          );
        })}
        {drag?.kind === 'connect' && cursor && byKey.get(drag.from) && (
          <line
            x1={pos(byKey.get(drag.from)!).x + NODE_W}
            y1={pos(byKey.get(drag.from)!).y + NODE_H / 2}
            x2={cursor.x}
            y2={cursor.y}
            className="anim-rubber"
          />
        )}
        {nodes.map((n) => {
          const p = pos(n);
          const selected = n.key === selectedNode;
          const canConnect = n.kind === 'state' || n.kind === 'any' || n.kind === 'entry';
          return (
            <g
              key={n.key}
              data-node={n.key}
              transform={`translate(${p.x} ${p.y})`}
              className={`anim-node kind-${n.kind}${selected ? ' is-selected' : ''}${n.isDefault ? ' is-default' : ''}${n.active ? ' is-active' : ''}`}
            >
              <rect width={NODE_W} height={NODE_H} rx={n.kind === 'machine' ? 12 : 6} className="anim-node-box" />
              <text x={NODE_W / 2} y={n.detail ? 16 : NODE_H / 2 + 4} className="anim-node-label">
                {n.label.length > 22 ? `${n.label.slice(0, 21)}…` : n.label}
              </text>
              {n.detail && (
                <text x={NODE_W / 2} y={31} className="anim-node-detail">
                  {n.detail.length > 26 ? `${n.detail.slice(0, 25)}…` : n.detail}
                </text>
              )}
              {canConnect && <circle data-port={n.key} cx={NODE_W} cy={NODE_H / 2} r={6} className="anim-port" />}
            </g>
          );
        })}
      </g>
    </svg>
  );
}
